import { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Upload, FileText, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface FileItem {
  file: File;
  name: string;
}

interface DocumentUploadZoneProps {
  label: string;
  description: string;
  files: FileItem[];
  onAdd: (files: FileItem[]) => void;
  onRemove: (index: number) => void;
}

export function DocumentUploadZone({
  label,
  description,
  files,
  onAdd,
  onRemove,
}: DocumentUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const newItems: FileItem[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const f = fileList[i];
      if (f.type.includes("pdf") || f.name.endsWith(".pdf")) {
        if (f.size <= 20 * 1024 * 1024) {
          newItems.push({ file: f, name: f.name });
        }
      }
    }
    if (newItems.length === 0) {
      toast({
        title: "Invalid files",
        description: "Please upload PDF files (max 20MB each).",
        variant: "destructive",
      });
      return;
    }
    onAdd(newItems);
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-foreground">{label}</h4>
      <p className="text-xs text-muted-foreground">{description}</p>
      <Card
        className={`border-2 border-dashed transition-all cursor-pointer ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-muted/30"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
      >
        <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
          <Upload className="w-5 h-5 text-muted-foreground mb-2" />
          <p className="text-xs text-muted-foreground">
            Drop PDFs here or <span className="text-primary underline">browse</span>
          </p>
        </div>
      </Card>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div
              key={i}
              className="flex items-center justify-between text-xs bg-muted/50 rounded px-2.5 py-1.5"
            >
              <div className="flex items-center gap-1.5 truncate">
                <FileText className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                <span className="truncate font-mono">{f.name}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(i);
                }}
                className="text-muted-foreground hover:text-destructive ml-2"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export type { FileItem };
